<script lang="ts">
    import { getCachedEntries } from "../../../api"
    import { spaLink } from "../../actions"
    import { location } from "../../store"
    import { blur } from "svelte/transition"
    import MedialibImage from "../MedialibImage.svelte"
    import { applyFilterFromHash, filter2Hash } from "../../utils"
    import InView from "../InView.svelte"
    import LinkList from "../LinkList.svelte"
    import { add2Cart } from "../CartButton.svelte"

    export let block: ContentBlockEntry

    let loading = true
    let products: ProductEntry[]
    let solutions: SolutionEntry[]
    let solutionsMap: { [key: string]: SolutionEntry } = {}
    let categories: CategoryEntry[]
    let categoriesMap: { [key: string]: CategoryEntry } = {}
    let lines: LineEntry[]
    let linesMap: { [key: string]: LineEntry } = {}

    async function load() {
        loading = true
        categories = await getCachedEntries("category", null, "sort")
        categories.forEach((category) => {
            categoriesMap[category.ident] = category
        })
        products = (
            await getCachedEntries(
                "product",
                {
                    active: true,
                    $or: [
                        { releaseDate: { $exists: false } },
                        { releaseDate: { $lte: { $date: new Date().toISOString() } } },
                    ],
                },
                "-releaseDate",
                null,
                null,
                null,
                { si: "1" }
            )
        )?.sort((a, b) => {
            // sort by category sort field
            if (a.categories?.length && b.categories?.length) {
                return categoriesMap[a.categories[0]]?.sort - categoriesMap[b.categories[0]]?.sort
            }
        })
        solutions = await getCachedEntries("solution", null, "sort")
        solutions.forEach((solution) => {
            solutionsMap[solution.ident] = solution
        })
        lines = await getCachedEntries("line", null, "sort")
        lines.forEach((line) => {
            linesMap[line.ident] = line
        })

        loading = false
    }

    load()

    function html2TextTeaser(text: string, maxChars = 70): string {
        return text?.replace(/<[^>]*>?/gm, "").substring(0, maxChars) || ""
    }

    let productFilter: { [key: string]: string[] } = {
        solution: [],
        category: [],
        line: [],
    }

    let filterInitialized = false

    // first apply filter from initial hash
    $: if (filterInitialized) {
        filter2Hash(productFilter, true)
    }

    let filteredProducts: ProductEntry[]
    function applyFilter(hash: string) {
        const f = applyFilterFromHash(
            hash,
            { solution: "solutions", category: "categories", line: "lines" },
            products,
            () => {
                if (typeof window !== "undefined") {
                    setTimeout(() => {
                        scrollTargetContainer?.scrollIntoView({
                            behavior: "instant",
                            block: "start",
                            inline: "nearest",
                        })
                        setTimeout(() => {
                            scrollTargetContainer?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                                inline: "nearest",
                            })
                        }, 600) // after in-view scroll animation
                    }, 10)
                }
            }
        )
        filteredProducts = f.filteredItems
        if (f.newFilter) {
            productFilter = f.newFilter
        }
        filterInitialized = true
    }

    $: if (!loading) {
        applyFilter($location.hash)
    }

    let scrollTargetContainer: HTMLDivElement

    let openFilter = false
    let openCatFilter = false
    let openTypeFilter = false
</script>

<!-- position 100px above -->
<div style="position:relative;">
    <div bind:this="{scrollTargetContainer}" style="position: absolute; top: -100px"></div>
</div>

<div class="container" data-block-type="{block.type}">
    <div class="grid align-start">
        <div class="col-md-3 gray-gradient col-grid gap-s filter-col">
            {#if solutions?.length}
                <div class="col-12 filters-wrapper">
                    <button
                        class="h4 filter-headline vp-m"
                        aria-expanded="{openFilter}"
                        on:click="{() => (openFilter = !openFilter)}"
                        >Solution
                        <span class="filter-number">{productFilter.solution.length}</span></button
                    >
                    <div class="option-group gap-s" class:open="{openFilter}">
                        {#each solutions as solution}
                            <input
                                type="checkbox"
                                id="solution_{solution.ident}"
                                bind:group="{productFilter.solution}"
                                value="{solution.ident}"
                            />
                            <label for="solution_{solution.ident}">
                                <div class="checkbox-pill">
                                    <span class="si-{solution.ident}"></span>
                                    {solution.name || ""}
                                </div>
                            </label>
                        {/each}
                    </div>
                </div>
            {/if}
            {#if categories?.length}
                <div class="col-12 filters-wrapper">
                    <button
                        class="h4 filter-headline vp-m"
                        aria-expanded="{openCatFilter}"
                        on:click="{() => (openCatFilter = !openCatFilter)}"
                        >Categories
                        <span class="filter-number">{productFilter.category.length}</span></button
                    >
                    <div class="option-group gap-s" class:open="{openCatFilter}">
                        {#each categories as category}
                            <input
                                type="checkbox"
                                id="category_{category.ident}"
                                bind:group="{productFilter.category}"
                                value="{category.ident}"
                            />
                            <label for="category_{category.ident}">{category.namePlural || category.name || ""}</label>
                        {/each}
                    </div>
                </div>
            {/if}
            {#if lines?.length}
                <div class="col-12 filters-wrapper">
                    <button
                        class="h4 filter-headline vp-m"
                        aria-expanded="{openTypeFilter}"
                        on:click="{() => (openTypeFilter = !openTypeFilter)}"
                        >Type
                        <span class="filter-number">{productFilter.line.length}</span></button
                    >
                    <div class="option-grid gap-s" class:open="{openTypeFilter}">
                        {#each lines as line}
                            <input
                                type="checkbox"
                                id="line_{line.ident}"
                                bind:group="{productFilter.line}"
                                value="{line.ident}"
                            />
                            <label for="line_{line.ident}">
                                <div class="checkbox-logo">
                                    <figure>
                                        <img src="/assets/img/{line.ident}-logo.svg" alt="{line.name || ''}" />
                                    </figure>
                                </div>
                            </label>
                        {/each}
                    </div>
                </div>
            {/if}
        </div>
        <div class="col-md-9">
            <div class="grid grid-bleed product-cards vp-xl hp-xl gap-m">
                {#each filteredProducts || [] as product (product.id)}
                    <!-- animation on filtering -->
                    <article class="col-auto product-card" transition:blur="{{ amount: 10 }}">
                        <header>
                            <img
                                class="background-detail"
                                src="/assets/img/products-detailheader.jpg"
                                alt="Product Detail"
                            />
                            <figure>
                                <InView>
                                    <MedialibImage id="{product.teaserimage}" />
                                </InView>
                            </figure>
                            {#if product.solutions?.length}
                                <ul class="solution-grid">
                                    {#each product.solutions as solutionIdent}
                                        {#if solutionsMap[solutionIdent]}
                                            <li>
                                                <span class="si-{solutionIdent}"></span>
                                            </li>
                                        {/if}
                                    {/each}
                                </ul>
                            {/if}
                            {#if product.lines?.length}
                                <ul class="type-grid">
                                    {#each product.lines as lineIdent}
                                        {#if linesMap[lineIdent]}
                                            <li>
                                                <figure>
                                                    <img
                                                        src="/assets/img/{lineIdent}-logo.svg"
                                                        alt="{linesMap[lineIdent]?.name || ''}"
                                                    />
                                                </figure>
                                            </li>
                                        {/if}
                                    {/each}
                                </ul>
                            {/if}
                        </header>
                        <div class="content">
                            <div class="badge">
                                {#if product.categories?.length}
                                    {#each product.categories as categoryIdent}
                                        {#if categoriesMap[categoryIdent]}
                                            <span>{categoriesMap[categoryIdent]?.name || ""}</span>
                                        {/if}
                                    {/each}
                                {/if}
                                {#if product.number}
                                    <span>Art.-No. {product.number}</span>
                                {/if}
                            </div>
                            <h3>{product.title || ""}</h3>
                            <div class="description">
                                {#if product.price_net}
                                    <div class="price">
                                        <b
                                            >{new Intl.NumberFormat("en-EN", {
                                                style: "currency",
                                                currency: "EUR",
                                            }).format(product.price_net)}</b
                                        >
                                        excl. VAT<br />
                                        <small
                                            >{new Intl.NumberFormat("en-EN", {
                                                style: "currency",
                                                currency: "EUR",
                                            }).format(product.price_net * 1.19)} incl. 19% VAT</small
                                        >
                                    </div>
                                {/if}
                                {@html html2TextTeaser(product.text, product.price_net ? 40 : 100)}...
                            </div>
                            <nav class="vertical">
                                <a
                                    use:spaLink
                                    href="/products/{product.slug || product.id}"
                                    class="cta btn-secondary btn-arrow-right">More details</a
                                >
                            </nav>
                        </div>
                        {#if product.links?.length || product.externalData?.isAvailable}
                            <nav class="product-nav">
                                {#if product.externalData?.isAvailable}
                                    {#if product.externalData?.customAttributes?.length || product.externalData.bomItems?.length || product.externalData.properties?.sportidentIdRangeMax}
                                        <!-- TODO: icon -->
                                        <a
                                            use:spaLink
                                            href="/products/{product.slug || product.id}#configurator"
                                            class="cta btn-configure">Configure product</a
                                        >
                                    {:else}
                                        <!-- TODO: icon -->
                                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                                        <!-- svelte-ignore a11y-missing-attribute -->
                                        <a
                                            href="/cart"
                                            on:click|preventDefault="{() => {
                                                add2Cart(product, 1)
                                            }}"
                                            class="cta btn-cardReservation">Add to cart</a
                                        >
                                    {/if}
                                {/if}
                                <LinkList links="{product.links}" />
                            </nav>
                        {/if}
                    </article>
                {/each}
            </div>
        </div>
    </div>
</div>
